import { Box, Grid, GridItem } from "@chakra-ui/react"
import type {
  QuestionAnalyticsDetail,
  QuestionSavedAnalyticsPublic,
} from "../../../client"
import AnswerDistributionChart from "./AnswerDistributionChart"
import CorrectnessOverTimeChart from "./CorrectnessOverTimeChart"
import DateRangeSelector from "./DateRangeSelector"
import DifficultyIndicator from "./DifficultyIndicator"
import DiscriminationIndicator from "./DiscriminationIndicator"
import FeedbackSummaryCard from "./FeedbackSummaryCard"
import PerformanceByQuizChart from "./PerformanceByQuizChart"
import { SavedQuestionActivityCard } from "./SavedQuestionActivityCard"
import SummaryStatsCard from "./SummaryStatsCard"
import TimeDistributionChart from "./TimeDistributionChart"

interface QuestionAnalyticsDashboardProps {
  analytics: QuestionAnalyticsDetail
  questionText?: string | null
  activeDatePreset: "7d" | "30d" | "90d" | "all"
  onDatePresetChange: (preset: "7d" | "30d" | "90d" | "all") => void
  savedActivity?: QuestionSavedAnalyticsPublic
}

export default function QuestionAnalyticsDashboard({
  analytics,
  questionText,
  activeDatePreset,
  onDatePresetChange,
  savedActivity,
}: QuestionAnalyticsDashboardProps) {
  return (
    <Box>
      {/* Date Range Selector */}
      <Box mb={6}>
        <DateRangeSelector
          activePreset={activeDatePreset}
          onPresetChange={onDatePresetChange}
        />
      </Box>

      {/* Summary Stats - Top Row */}
      <Grid
        templateColumns={{ base: "1fr", lg: "repeat(3, 1fr)" }}
        gap={6}
        mb={6}
      >
        <GridItem>
          <SummaryStatsCard summary={analytics.summary} />
        </GridItem>
        <GridItem>
          <DifficultyIndicator
            difficultyScore={analytics.difficulty_score}
            difficultyLevel={analytics.summary.difficulty_level}
          />
        </GridItem>
        <GridItem>
          <DiscriminationIndicator
            discriminationIndex={analytics.discrimination_index}
            discriminationLevel={analytics.summary.discrimination_level}
          />
        </GridItem>
      </Grid>

      {/* Charts - Main Content */}
      <Grid
        templateColumns={{ base: "1fr", lg: "repeat(2, 1fr)" }}
        gap={6}
        mb={6}
      >
        <GridItem>
          <TimeDistributionChart data={analytics.time_distribution} />
        </GridItem>
        <GridItem>
          <AnswerDistributionChart
            data={analytics.answer_distribution}
            questionText={questionText}
          />
        </GridItem>
        <GridItem>
          <CorrectnessOverTimeChart data={analytics.correctness_over_time} />
        </GridItem>
        <GridItem>
          <PerformanceByQuizChart data={analytics.performance_by_quiz} />
        </GridItem>
      </Grid>

      {/* Feedback Summary */}
      <Grid templateColumns={{ base: "1fr", lg: "repeat(2, 1fr)" }} gap={6}>
        <FeedbackSummaryCard feedback={analytics.feedback_summary} />
        {savedActivity && (
          <SavedQuestionActivityCard activity={savedActivity} />
        )}
      </Grid>
    </Box>
  )
}
